import { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import SignatureModal from "../Common/SignatureModal";
import PermissionWrapper from "../PermissionWrapper";
import WirDocumentPreview from "./WirDocumentPreview";

const WirList = () => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth?.userDetails || {});
    const [wirs, setWirs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [registerExporting, setRegisterExporting] = useState(false);
    const [searchParams] = useSearchParams();

    const [selectedWir, setSelectedWir] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showSignModal, setShowSignModal] = useState(false);
    const [approveId, setApproveId] = useState(null);

    const [workOrders, setWorkOrders] = useState([]);
    const [projects, setProjects] = useState([]);

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
    });

    const initialWorkOrder = searchParams.get("workOrder") || "";
    const initialProject = searchParams.get("project") || "";

    const [filters, setFilters] = usePersistentFilters("wir_list_filters", {
        search: "",
        status: "",
        workOrder: initialWorkOrder,
        project: initialProject,
        fromDate: "",
        toDate: "",
    });

    useEffect(() => {
        fetchWirs();
    }, [pagination.currentPage, pagination.limit, filters]);

    useEffect(() => {
        fetchDropdowns();
    }, []);

    const fetchDropdowns = async () => {
        try {
            const [woRes, prRes] = await Promise.all([
                api.get("/work-orders?limit=1000"),
                api.get("/projects?limit=1000"),
            ]);
            if (woRes.data.success) setWorkOrders(woRes.data.data || []);
            if (prRes.data.success) setProjects(prRes.data.data || []);
        } catch {
            console.error("Failed to fetch dropdowns");
        }
    };

    const fetchWirs = async () => {
        try {
            setLoading(true);

            const params = {
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.search && { search: filters.search }),
                ...(filters.status && { status: filters.status }),
                ...(filters.workOrder && { workOrder: filters.workOrder }),
                ...(filters.project && { project: filters.project }),
                ...(filters.fromDate && { fromDate: filters.fromDate }),
                ...(filters.toDate && { toDate: filters.toDate }),
            };

            const res = await api.get("/wir", { params });

            if (res.data.success) {
                setWirs(res.data.data);
                setPagination((prev) => ({
                    ...prev,
                    totalPages: res.data.pagination.totalPages,
                    totalCount: res.data.pagination.totalCount,
                    currentPage: res.data.pagination.currentPage,
                }));
            }
        } catch (error) {
            swal("Error", "Unable to load WIR data", "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setPagination((prev) => ({ ...prev, currentPage: page }));
    };

    const getStatusBadge = (status) => {
        const map = {
            Draft: "secondary",
            Submitted: "warning",
            Approved: "success",
            Rejected: "danger",
            Resubmitted: "info",
        };
        return map[status] || "primary";
    };

    const handleView = async (wir) => {
        try {
            const res = await api.get(`/wir/${wir._id}`);
            if (res.data.success) {
                setSelectedWir(res.data.data);
                setShowModal(true);
            }
        } catch (err) {
            swal("Error", "Unable to load WIR details", "error");
        }
    };

    const handleDelete = async (id) => {
        const confirm = await swal({
            title: "Are you sure?",
            text: "This WIR will be permanently deleted",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        });

        if (confirm) {
            try {
                const res = await api.delete(`/wir/${id}`);
                if (res.data.success) {
                    swal("Deleted", "WIR has been removed", "success");
                    fetchWirs();
                }
            } catch (error) {
                swal("Error", "Failed to delete WIR", "error");
            }
        }
    };

    const handleFilterChange = (name, value) => {
        setFilters((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "project") {
                if (value && prev.workOrder) {
                    const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
                    const currentWoProjId = currentWo?.project?._id || currentWo?.project;
                    if (currentWoProjId && String(currentWoProjId) !== String(value)) {
                        next.workOrder = "";
                    }
                }
            } else if (name === "workOrder") {
                if (value) {
                    const selectedWo = workOrders.find((w) => String(w._id) === String(value));
                    const autoProjId = selectedWo?.project?._id || selectedWo?.project;
                    if (autoProjId) {
                        next.project = String(autoProjId);
                    }
                }
            }
            return next;
        });
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };

    const handleApprove = async (id) => {
        const confirm = await swal({
            title: "Approve WIR?",
            text: "Signature is required for approval.",
            icon: "info",
            buttons: ["Cancel", "Provide Signature"],
        });

        if (!confirm) return;

        setApproveId(id);
        setShowSignModal(true);
    };

    const handleSaveSignature = async (file) => {
        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("consultantSignature", file);

            const res = await api.patch(`/wir/${approveId}/approve`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.data.success) {
                swal("Approved!", "WIR approved successfully", "success");
                setShowSignModal(false);
                setApproveId(null);
                fetchWirs();
            }
        } catch (err) {
            swal(
                "Error",
                err.response?.data?.message || "Failed to approve WIR",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async (id) => {
        const rejectionReason = prompt("Enter rejection reason:");
        if (!rejectionReason) return;

        try {
            const res = await api.patch(`/wir/${id}/reject`, { rejectionReason });
            if (res.data.success) {
                swal("Rejected!", "WIR rejected successfully", "success");
                fetchWirs();
            }
        } catch (err) {
            swal("Error", "Failed to reject", "error");
        }
    };

    const handleExportExcel = async (wir) => {
        try {
            const res = await api.get(`/wir/${wir._id}/excel`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `WIR-${wir.wirNumber || wir._id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            swal("Error", "Failed to export Excel", "error");
        }
    };

    const handleExportRegisterExcel = async () => {
        try {
            setRegisterExporting(true);
            const params = {
                ...(filters.search && { search: filters.search }),
                ...(filters.status && { status: filters.status }),
                ...(filters.workOrder && { workOrder: filters.workOrder }),
                ...(filters.project && { project: filters.project }),
                ...(filters.fromDate && { fromDate: filters.fromDate }),
                ...(filters.toDate && { toDate: filters.toDate }),
            };

            const res = await api.get("/wir/register-excel", {
                params,
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `WIR-Register-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            swal("Error", "Failed to export WIR register", "error");
        } finally {
            setRegisterExporting(false);
        }
    };

    const filteredWorkOrders = filters.project
        ? workOrders.filter(
            (wo) => String(wo.project?._id || wo.project) === String(filters.project)
        )
        : workOrders;

    return (
        <Fragment>
            <PageTitle activeMenu="WIR" motherMenu="Quality" />

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <Card.Title>Work Inspection Requests</Card.Title>

                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <PermissionWrapper user={user} required={["read:wirs"]}>
                                    <Button
                                        variant="outline-success"
                                        onClick={handleExportRegisterExcel}
                                        disabled={registerExporting}
                                    >
                                        {registerExporting ? (
                                            <span className="spinner-border spinner-border-sm me-2" />
                                        ) : (
                                            <i className="fas fa-file-excel me-2" />
                                        )}
                                        WIR Register
                                    </Button>
                                </PermissionWrapper>

                                <PermissionWrapper user={user} required={["create:wirs"]}>
                                    <Link
                                        className="btn btn-primary"
                                        to={
                                            filters.workOrder
                                                ? `/wir/add?workOrder=${filters.workOrder}`
                                                : "/wir/add"
                                        }
                                    >
                                        <i className="fas fa-plus me-2" /> Add WIR
                                    </Link>
                                </PermissionWrapper>
                            </div>
                        </Card.Header>

                        <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
                            <div className="d-flex flex-column" style={{ width: "180px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    Search WIR
                                </label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    placeholder="Search WIR..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange("search", e.target.value)}
                                />
                            </div>

                            <div className="d-flex flex-column" style={{ width: "145px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    Status
                                </label>
                                <Form.Select
                                    size="sm"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange("status", e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Draft">Draft</option>
                                    <option value="Submitted">Submitted</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Resubmitted">Resubmitted</option>
                                </Form.Select>
                            </div>

                            <div className="d-flex flex-column" style={{ width: "180px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    Project
                                </label>
                                <Form.Select
                                    size="sm"
                                    value={filters.project}
                                    onChange={(e) => handleFilterChange("project", e.target.value)}
                                >
                                    <option value="">All Projects</option>
                                    {projects.map((p) => (
                                        <option key={p._id} value={p._id}>
                                            {p.projectCode ? `${p.projectCode} — ` : ""}{p.projectName}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="d-flex flex-column" style={{ width: "200px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    Work Order
                                </label>
                                <Form.Select
                                    size="sm"
                                    value={filters.workOrder}
                                    onChange={(e) => handleFilterChange("workOrder", e.target.value)}
                                >
                                    <option value="">All Work Orders</option>
                                    {filteredWorkOrders.map((wo) => (
                                        <option key={wo._id} value={wo._id}>
                    {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
                  </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div className="d-flex flex-column" style={{ width: "135px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    From Date
                                </label>
                                <Form.Control
                                    size="sm"
                                    type="date"
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                                />
                            </div>

                            <div className="d-flex flex-column" style={{ width: "135px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    To Date
                                </label>
                                <Form.Control
                                    size="sm"
                                    type="date"
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange("toDate", e.target.value)}
                                />
                            </div>

                            <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
                                <label className="text-muted mb-1 fs-12 font-weight-bold">
                                    Show
                                </label>
                                <Form.Select
                                    size="sm"
                                    value={pagination.limit}
                                    onChange={(e) =>
                                        setPagination((prev) => ({
                                            ...prev,
                                            currentPage: 1,
                                            limit: Number(e.target.value),
                                        }))
                                    }
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </Form.Select>
                            </div>
                        </div>

                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" />
                                </div>
                            ) : wirs.length === 0 ? (
                                <div className="text-center py-5">
                                    <h5>No WIR Found</h5>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>WIR No</th>
                                                <th>Project</th>
                                                <th>Work Order</th>
                                                <th>Location</th>
                                                <th>Activity</th>
                                                <th>Inspection Date</th>
                                                <th>JMR References</th>
                                                <th>Client Decision</th>
                                                <th>Re-inspection</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {wirs.map((wir, i) => (
                                                <tr key={wir._id}>
                                                    <td>
                                                        {(pagination.currentPage - 1) * pagination.limit +
                                                            i +
                                                            1}
                                                    </td>

                                                    <td>
                                                        <strong>{wir.wirNumber}</strong>
                                                    </td>

                                                    <td>
                                                        {wir.project?.projectName || wir.project?.projectCode || "—"}
                                                    </td>

                                                    <td>
                                                        {wir.workOrder?.woNo || wir.workOrder?.woNumber || "—"}
                                                    </td>

                                                    <td>{wir.location || "—"}</td>

                                                    <td>{wir.activityDescription || "—"}</td>

                                                    <td>
                                                        {wir.inspectionDate
                                                            ? new Date(wir.inspectionDate).toLocaleDateString()
                                                            : "—"}
                                                    </td>

                                                    <td>
                                                        {wir.jmrReferences?.length > 0
                                                            ? wir.jmrReferences
                                                                .map((ref) => ref.jmrNumber)
                                                                .join(", ")
                                                            : "—"}
                                                    </td>

                                                    <td>
                                                        {wir.clientDecision ? (
                                                            <Badge
                                                                bg={
                                                                    wir.clientDecision.includes("APPROVED")
                                                                        ? "success"
                                                                        : "danger"
                                                                }
                                                                className="fs-12"
                                                            >
                                                                {wir.clientDecision.split(" - ")[0]}
                                                            </Badge>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td>
                                                        {wir.reinspectionRequired ? (
                                                            <Badge bg="warning" className="fs-12">
                                                                Yes
                                                            </Badge>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td>
                                                        <Badge bg={getStatusBadge(wir.approvalStatus)}>
                                                            {wir.approvalStatus}
                                                        </Badge>
                                                    </td>

                                                    <td>
                                                        <div className="d-flex align-items-center gap-1 flex-nowrap">
                                                            <PermissionWrapper user={user} required={["read:wirs"]}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-primary"
                                                                    title="View"
                                                                    onClick={() => handleView(wir)}
                                                                >
                                                                    <i className="fas fa-eye" />
                                                                </Button>
                                                            </PermissionWrapper>

                                                            <PermissionWrapper user={user} required={["read:wirs"]}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-success"
                                                                    title="Export Excel"
                                                                    onClick={() => handleExportExcel(wir)}
                                                                >
                                                                    <i className="fas fa-file-excel" />
                                                                </Button>
                                                            </PermissionWrapper>

                                                            <PermissionWrapper user={user} required={["edit:wirs"]}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-secondary"
                                                                    title="Edit"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            filters.workOrder
                                                                                ? `/wir/edit/${wir._id}?workOrder=${filters.workOrder}`
                                                                                : `/wir/edit/${wir._id}`
                                                                        )
                                                                    }
                                                                >
                                                                    <i className="fas fa-edit" />
                                                                </Button>
                                                            </PermissionWrapper>

                                                            {(wir.approvalStatus === "Submitted" ||
                                                                wir.approvalStatus === "Resubmitted") && (
                                                                    <PermissionWrapper user={user} required={["approve:wirs"]}>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="success"
                                                                            title="Approve"
                                                                            onClick={() => handleApprove(wir._id)}
                                                                        >
                                                                            Approve
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="danger"
                                                                            title="Reject"
                                                                            onClick={() => handleReject(wir._id)}
                                                                        >
                                                                            Reject
                                                                        </Button>
                                                                    </PermissionWrapper>
                                                                )}

                                                            <PermissionWrapper user={user} required={["delete:wirs"]}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    title="Delete"
                                                                    onClick={() => handleDelete(wir._id)}
                                                                >
                                                                    <i className="fas fa-trash" />
                                                                </Button>
                                                            </PermissionWrapper>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <AppPagination
                                        currentPage={pagination.currentPage}
                                        totalPages={pagination.totalPages}
                                        totalCount={pagination.totalCount}
                                        itemsLength={wirs.length}
                                        limit={pagination.limit}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <SignatureModal
                show={showSignModal}
                onHide={() => {
                    setShowSignModal(false);
                    setApproveId(null);
                }}
                onSave={handleSaveSignature}
                title="Consultant Signature for WIR Approval"
            />

            {selectedWir && (
                <Modal
                    centered
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    size="lg"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>WIR Details</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={6}>
                                <strong>WIR Number:</strong> {selectedWir.wirNumber}
                            </Col>
                            <Col md={6}>
                                <strong>Inspection Date:</strong>{" "}
                                {selectedWir.inspectionDate
                                    ? new Date(selectedWir.inspectionDate).toLocaleDateString()
                                    : "—"}
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <strong>Project:</strong>{" "}
                                {selectedWir.project?.projectCode
                                    ? `${selectedWir.project.projectCode} — `
                                    : ""}
                                {selectedWir.project?.projectName || "—"}
                            </Col>
                            <Col md={6}>
                                <strong>Work Order:</strong>{" "}
                                {selectedWir.workOrder?.woNo || "—"} {selectedWir.workOrder?.scope || ""}
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <strong>Location:</strong> {selectedWir.location || "—"}
                            </Col>
                            <Col md={6}>
                                <strong>Status:</strong> {selectedWir.approvalStatus}
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <strong>Activity Description:</strong>{" "}
                                {selectedWir.activityDescription || "—"}
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <strong>Drawing References:</strong>{" "}
                                {selectedWir.drawingReferences?.length > 0
                                    ? selectedWir.drawingReferences.join(", ")
                                    : "—"}
                            </Col>
                        </Row>

                        {selectedWir.jmrReferences?.length > 0 && (
                            <>
                                <h5 className="mt-4">JMR References</h5>
                                <Table bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>JMR Number</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedWir.jmrReferences.map((ref, idx) => (
                                            <tr key={idx}>
                                                <td>{ref.jmrNumber}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}

                        {[
                            { label: "Consultant Signature", doc: selectedWir.consultantSignature },
                            { label: "Inspector Signature", doc: selectedWir.inspectorDetails?.signature },
                            { label: "Inspector Stamp", doc: selectedWir.inspectorDetails?.stamp },
                            { label: "Client Signature", doc: selectedWir.clientRepresentativeDetails?.signature },
                            { label: "Client Stamp", doc: selectedWir.clientRepresentativeDetails?.stamp },
                        ].some((item) => item.doc?.fileUrl) && (
                                <>
                                    <h5 className="mt-4">Uploaded Signatures / Stamps</h5>
                                    <Table bordered size="sm">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Preview</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { label: "Consultant Signature", doc: selectedWir.consultantSignature },
                                                { label: "Inspector Signature", doc: selectedWir.inspectorDetails?.signature },
                                                { label: "Inspector Stamp", doc: selectedWir.inspectorDetails?.stamp },
                                                { label: "Client Signature", doc: selectedWir.clientRepresentativeDetails?.signature },
                                                { label: "Client Stamp", doc: selectedWir.clientRepresentativeDetails?.stamp },
                                            ]
                                                .filter((item) => item.doc?.fileUrl)
                                                .map((item) => (
                                                    <tr key={item.label}>
                                                        <td>{item.label}</td>
                                                        <td>
                                                            <WirDocumentPreview doc={item.doc} />
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </Table>
                                </>
                            )}

                        {selectedWir.supportingDocuments?.length > 0 && (
                            <>
                                <h5 className="mt-4">Supporting Documents</h5>
                                <Table bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>File</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedWir.supportingDocuments.map((doc, idx) => (
                                            <tr key={idx}>
                                                <td>{doc.documentType}</td>
                                                <td>
                                                    <WirDocumentPreview doc={doc} />
                                                </td>
                                                <td>{doc.description || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}

                        {selectedWir.inspectionRemarks && (
                            <Row className="mb-3 mt-3">
                                <Col md={12}>
                                    <strong>Inspection Remarks:</strong>{" "}
                                    {selectedWir.inspectionRemarks}
                                </Col>
                            </Row>
                        )}

                        {selectedWir.rejectionReason && (
                            <Row className="mb-3 mt-3">
                                <Col md={12}>
                                    <strong>Rejection Reason:</strong>{" "}
                                    <span className="text-danger">{selectedWir.rejectionReason}</span>
                                </Col>
                            </Row>
                        )}

                        {selectedWir.remarks && (
                            <Row className="mb-3 mt-3">
                                <Col md={12}>
                                    <strong>Remarks:</strong> {selectedWir.remarks}
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Fragment>
    );
};

export default WirList;
